// =============================================================
// blockUpload.js — resumable, chunked browser → Azure Blob uploader for the DAM
// module.
//
// Replaces the old single "Put Blob" fetch (which capped at ~256 MB, had no
// progress, no retry, and couldn't survive a network drop or reload). Instead we
// slice the file into blocks, PUT each block (`comp=block`), then commit the
// block list (`comp=blocklist`) — Azure's native block-blob mechanism. Staged
// (uncommitted) blocks live server-side for ~7 days, so an interrupted upload
// resumes by re-staging only the blocks it hadn't finished.
//
// Pure module — NO imports (no MSAL / no `api` client), so both the authenticated
// path (damApi.js) and the public share bundle (public/publicApi.js) can use it
// without dragging the MSAL client into the public bundle. Progress uses
// XMLHttpRequest (not fetch — fetch exposes no upload-progress events).
//
// Part of the removable DAM module (see DAM_MODULE.md).
// =============================================================

const MiB = 1024 * 1024;
const DEFAULT_BLOCK_SIZE = 8 * MiB;   // 8 MiB blocks
const MAX_BLOCKS = 45000;             // Azure hard limit is 50,000 — leave headroom
const DEFAULT_CONCURRENCY = 4;        // parallel block PUTs
const MAX_BLOCK_ATTEMPTS = 5;         // per-block retries before giving up
const RESUME_PREFIX = 'dam-resume:';

// Block size for a file, adaptively grown so a huge file still fits under the
// 45k-block ceiling. 8 MiB × 45k ≈ 360 GB before the adaptive path kicks in.
export function computeBlockSize(fileSize) {
  let bs = DEFAULT_BLOCK_SIZE;
  if (fileSize / bs > MAX_BLOCKS) {
    bs = Math.ceil(fileSize / MAX_BLOCKS / MiB) * MiB;
  }
  return bs;
}

// Identifies a File for resume: same name+size+lastModified → same upload.
export function fileSignature(file) {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

// --- localStorage resume state (best-effort; never throws) -----------------
// value: { assetId, blobName, sasUrl, sasExpiresAt, blockSize, uploadedIndices:[] }
export function loadResumeState(sig) {
  try {
    const raw = localStorage.getItem(RESUME_PREFIX + sig);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function saveResumeState(sig, state) {
  try { localStorage.setItem(RESUME_PREFIX + sig, JSON.stringify(state)); } catch { /* quota / disabled */ }
}
export function clearResumeState(sig) {
  try { localStorage.removeItem(RESUME_PREFIX + sig); } catch { /* ignore */ }
}

// Parse the SAS expiry (`se=…` ISO timestamp) into an epoch-ms, or null.
export function sasExpiresAtFromUrl(sasUrl) {
  try {
    const se = new URL(sasUrl).searchParams.get('se');
    const t = se ? Date.parse(se) : NaN;
    return Number.isFinite(t) ? t : null;
  } catch { return null; }
}

// Deterministic, equal-length base64 block id (Azure requires uniform-length ids
// within a blob). Index-based so re-staging a block is idempotent on resume.
function blockIdFor(index) {
  return btoa(`blk-${String(index).padStart(6, '0')}`);
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// PUT one block via XHR, reporting in-flight bytes through onChunkProgress.
function putBlock(file, sasUrl, index, blockSize, onChunkProgress, signal) {
  return new Promise((resolve, reject) => {
    const start = index * blockSize;
    const end = Math.min(start + blockSize, file.size);
    const chunk = file.slice(start, end);
    const url = `${sasUrl}&comp=block&blockid=${encodeURIComponent(blockIdFor(index))}`;

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onChunkProgress(e.loaded); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Block ${index} failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error(`Block ${index} network error`));
    xhr.onabort = () => reject(Object.assign(new Error('aborted'), { aborted: true }));
    if (signal) {
      if (signal.aborted) { xhr.abort(); return; }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(chunk);
  });
}

// PUT the block-list XML to commit the blob. Uses fetch (no progress needed).
async function commitBlockList(sasUrl, totalBlocks, contentType) {
  const ids = Array.from({ length: totalBlocks }, (_, i) => blockIdFor(i));
  const xml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids.map((id) => `<Latest>${id}</Latest>`).join('')}</BlockList>`;
  const res = await fetch(`${sasUrl}&comp=blocklist`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/xml',
      ...(contentType ? { 'x-ms-blob-content-type': contentType } : {}),
    },
    body: xml,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw Object.assign(new Error(`Commit failed (${res.status})`), { status: res.status, body });
  }
}

// Run `worker(item)` over `items` with at most `concurrency` in flight.
async function runPool(items, worker, concurrency) {
  let cursor = 0;
  const runNext = async () => {
    while (cursor < items.length) {
      const item = items[cursor++];
      await worker(item);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, runNext));
}

// Stage all missing blocks then commit. Resolves once the blob is committed.
//
// opts:
//   sasUrl        — write SAS for the target blob
//   blockSize     — must match what the caller persisted (else resume mis-aligns)
//   concurrency   — parallel block PUTs (default 4)
//   contentType   — stored blob content type (set at commit)
//   alreadyStaged — indices already staged in a prior run (skipped)
//   onStaged(i)   — called after each block commits to storage (persist progress)
//   onProgress({ loaded, total }) — byte progress across the whole file
//   signal        — AbortSignal to cancel
export async function uploadFileInBlocks(file, opts = {}) {
  const {
    sasUrl,
    concurrency = DEFAULT_CONCURRENCY,
    contentType,
    alreadyStaged = [],
    onStaged,
    onProgress,
    signal,
  } = opts;
  const total = file.size;

  // Zero-byte file: a single empty Put Blob is simplest (no blocks to stage).
  if (total === 0) {
    const res = await fetch(sasUrl, {
      method: 'PUT',
      headers: { 'x-ms-blob-type': 'BlockBlob', ...(contentType ? { 'Content-Type': contentType } : {}) },
      body: new Blob([]),
    });
    if (!res.ok) throw new Error(`Upload failed (${res.status})`);
    onProgress?.({ loaded: 0, total: 0 });
    return { blockSize: 0, totalBlocks: 0 };
  }

  const blockSize = opts.blockSize || computeBlockSize(total);
  const totalBlocks = Math.ceil(total / blockSize);
  const sizeOfBlock = (i) => Math.min((i + 1) * blockSize, total) - i * blockSize;

  const staged = new Set((alreadyStaged || []).filter((i) => i >= 0 && i < totalBlocks));
  let completedBytes = 0;
  staged.forEach((i) => { completedBytes += sizeOfBlock(i); });

  const inflight = new Map(); // index -> bytes uploaded this attempt
  let lastEmit = 0;
  const emit = (force) => {
    const now = Date.now();
    if (!force && now - lastEmit < 80) return;
    lastEmit = now;
    let loaded = completedBytes;
    inflight.forEach((v) => { loaded += v; });
    onProgress?.({ loaded: Math.min(loaded, total), total });
  };
  emit(true);

  const stageOne = async (index) => {
    let attempt = 0;
    for (;;) {
      if (signal?.aborted) throw Object.assign(new Error('aborted'), { aborted: true });
      try {
        await putBlock(file, sasUrl, index, blockSize, (loaded) => { inflight.set(index, loaded); emit(false); }, signal);
        inflight.delete(index);
        completedBytes += sizeOfBlock(index);
        emit(true);
        onStaged?.(index);
        return;
      } catch (err) {
        inflight.delete(index);
        if (err?.aborted || attempt >= MAX_BLOCK_ATTEMPTS - 1) throw err;
        await sleep(Math.min(8000, 500 * 2 ** attempt));
        attempt++;
      }
    }
  };

  const pending = [];
  for (let i = 0; i < totalBlocks; i++) if (!staged.has(i)) pending.push(i);
  await runPool(pending, stageOne, concurrency);

  try {
    await commitBlockList(sasUrl, totalBlocks, contentType);
  } catch (err) {
    // A staged block may have been GC'd (e.g. very long-lived resume). Re-stage
    // everything once and commit again before surfacing the failure.
    if (err?.status === 400 && /InvalidBlockList/i.test(err.body || '')) {
      completedBytes = 0;
      inflight.clear();
      emit(true);
      await runPool(Array.from({ length: totalBlocks }, (_, i) => i), stageOne, concurrency);
      await commitBlockList(sasUrl, totalBlocks, contentType);
    } else {
      throw err;
    }
  }

  emit(true);
  return { blockSize, totalBlocks };
}

// Resolve a write SAS for `file`, resuming from a prior interrupted run when
// possible. `getUploadUrl(extra)` posts to the caller's upload-url endpoint
// (passing `{ resumeAssetId }` on refresh) and returns `{ assetId, blobName,
// sasUrl }`. On success the resume record is cleared. Returns `{ assetId }`.
export async function resumableUpload(file, { getUploadUrl, onProgress, signal } = {}) {
  const sig = fileSignature(file);
  const now = Date.now();
  let state = loadResumeState(sig);

  if (state && state.assetId && Array.isArray(state.uploadedIndices)) {
    if (state.sasUrl && state.sasExpiresAt && state.sasExpiresAt > now + 60000) {
      // Stored SAS still valid — resume against it.
    } else {
      // SAS lapsed — re-mint for the same asset/blob (blocks already staged stay
      // valid). If the asset is gone (committed/deleted), start fresh.
      try {
        const r = await getUploadUrl({ resumeAssetId: state.assetId });
        state = {
          ...state, assetId: r.assetId, blobName: r.blobName, sasUrl: r.sasUrl,
          sasExpiresAt: sasExpiresAtFromUrl(r.sasUrl),
          blockSize: state.blockSize || computeBlockSize(file.size),
        };
        saveResumeState(sig, state);
      } catch { clearResumeState(sig); state = null; }
    }
  } else {
    state = null;
  }

  if (!state) {
    const r = await getUploadUrl({});
    state = {
      assetId: r.assetId, blobName: r.blobName, sasUrl: r.sasUrl,
      sasExpiresAt: sasExpiresAtFromUrl(r.sasUrl),
      blockSize: computeBlockSize(file.size), uploadedIndices: [],
    };
    saveResumeState(sig, state);
  }

  await uploadFileInBlocks(file, {
    sasUrl: state.sasUrl,
    blockSize: state.blockSize,
    contentType: file.type || 'application/octet-stream',
    alreadyStaged: state.uploadedIndices,
    onStaged: (i) => {
      if (!state.uploadedIndices.includes(i)) state.uploadedIndices.push(i);
      saveResumeState(sig, state);
    },
    onProgress,
    signal,
  });

  clearResumeState(sig);
  return { assetId: state.assetId };
}
